<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import KeyValue from './KeyValue.vue';

const {
  label = null,
  labelIcon = null,
  value = null,
  valueIcon = null,
  labelProps = {},
  valueProps = {},
  canEdit = false,
  alwaysShowEditButton = false,
  isEditing = false,
  editButtonProps = {},
  cancelButtonProps = {},
  saveButtonProps = {}
} = defineProps<{
  label?: string | null;
  labelIcon?: string | null;
  value?: string | null;
  valueIcon?: string | null;
  labelProps?: Record<string, unknown>;
  valueProps?: Record<string, unknown>;
  canEdit?: boolean;
  alwaysShowEditButton?: boolean;
  isEditing?: boolean;
  editButtonProps?: Record<string, unknown>;
  cancelButtonProps?: Record<string, unknown>;
  saveButtonProps?: Record<string, unknown>;
}>();

const emit = defineEmits<{
  edit: [];
  cancel: [];
  save: [];
}>();

const { t } = useI18n();
</script>

<template>
  <div
    class="d-flex flex-column gap-2 editable-key-value"
    :class="{ '-always-show-edit-button': alwaysShowEditButton }">
    <div class="d-flex flex-row justify-space-between align-start flex-1">
      <key-value
        class="d-flex flex-column ga-2"
        style="flex: 1"
        :label-icon="labelIcon"
        :label-props="labelProps"
        :label="label"
        :value-icon="isEditing ? null : valueIcon"
        :value-props="valueProps"
        :value="isEditing ? null : value">
        <template #label>
          <slot name="label"></slot>
        </template>
        <template #value>
          <slot v-if="isEditing" name="editing-value"></slot>
          <slot v-else name="value">{{ value }}</slot>
        </template>
      </key-value>

      <div v-if="canEdit && !isEditing">
        <v-btn
          icon
          class="edit-button"
          size="small"
          variant="plain"
          v-bind="editButtonProps"
          :title="t('yard.tooltips.editData')"
          @click="emit('edit')">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="canEdit && isEditing" class="d-flex flex-column gap-2 flex-1">
      <div class="justify-end d-flex flex-row gap-2">
        <v-btn @click="emit('cancel')" variant="outlined" v-bind="cancelButtonProps">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <v-btn color="primary" variant="flat" @click="emit('save')" v-bind="saveButtonProps">
          {{ t('global.buttons.save') }}
        </v-btn>
      </div>
    </div>

    <slot></slot>
  </div>
</template>
<style lang="scss">
/* Always visible by default to cover for touch devices */
.editable-key-value .edit-button {
  opacity: 1;
  padding: 0;
  background-color: $white;
  transition: opacity 0.3s ease-in-out;
}

/* On hover-capable devices, fade in on hover */
@media (hover: hover) and (pointer: fine) {
  .editable-key-value:not(.-always-show-edit-button) .edit-button {
    opacity: 0;
  }
  .editable-key-value:hover .edit-button {
    opacity: 1;
  }
}

.editable-key-value form {
  // overrides a global padding-top set in the satellite library
  padding-top: 0 !important;
}
</style>
